import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

/** Slot du layout (ornement fin / long / large) — ajuste typo et remplissage du cadre. */
const ORNAMENT_DEFAULT = 'long';

const PanelRoot = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
`;

const ScrollContainer = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  position: relative;
  min-height: ${({ $ornament }) =>
    $ornament === 'large' ? '72px' : $ornament === 'fin' ? '200px' : '120px'};

  @media (max-width: 768px) {
    min-height: ${({ $ornament }) =>
      $ornament === 'large' ? '100px' : $ornament === 'fin' ? '220px' : '140px'};
  }
`;

const ScrollContent = styled.div`
  animation: ${({ $autoScroll }) => ($autoScroll ? "scroll 30s linear infinite" : "none")};
  
  @keyframes scroll {
    0% {
      transform: translateY(0);
    }
    100% {
      transform: translateY(-50%);
    }
  }
  
  &:hover {
    animation-play-state: paused;
  }
`;

const ZmanimItem = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 10px;
  padding: 4px 0;
  border-bottom: 1px solid rgba(45, 54, 83, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const ZmanimLabel = styled.div`
  color: #2d3653;
  font-family: 'IBM Plex Sans JP', sans-serif;
  font-size: ${({ $ornament }) => ($ornament === 'large' ? '11px' : $ornament === 'fin' ? '12px' : '13px')};
`;

const ZmanimValue = styled.div`
  color: #2d3653;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  text-align: right;
  font-size: ${({ $ornament }) => ($ornament === 'large' ? '11px' : $ornament === 'fin' ? '12px' : '13px')};
`;

const PanelTitle = styled.h2`
  color: #2d3653;
  text-align: center;
  font-family: 'Style Script', cursive;
  font-size: ${({ $ornament }) =>
    $ornament === 'large'
      ? 'clamp(14px, 1.2vw, 18px)'
      : $ornament === 'fin'
        ? 'clamp(15px, 1.3vw, 20px)'
        : 'clamp(16px, 1.5vw, 24px)'};
  margin-bottom: ${({ $ornament }) => ($ornament === 'large' ? '4px' : '10px')};
  margin-top: ${({ $ornament }) => ($ornament === 'large' ? '4px' : '10px')};

  @media (max-width: 768px) {
    margin-bottom: ${({ $ornament }) => ($ornament === 'large' ? '0' : '0')};
  }
`;

const ZmanimContainer = ({ zmanim, autoScroll = true, slotOrnament }) => {
  const { t } = useTranslation();
  const ornament =
    slotOrnament && slotOrnament !== 'none' ? slotOrnament : ORNAMENT_DEFAULT;

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <PanelRoot>
      <PanelTitle $ornament={ornament}>{t('display.zmanim')}</PanelTitle>
      <ScrollContainer $ornament={ornament}>
        <ScrollContent $autoScroll={autoScroll}>
          {zmanim?.zmanimFormatted && Object.entries(zmanim.zmanimFormatted).map(([key, value]) => (
            <ZmanimItem key={key}>
              <ZmanimLabel $ornament={ornament}>
                {key.replace(/([A-Z])/g, ' $1')
                   .replace(/^./, str => str.toUpperCase())}
              </ZmanimLabel>
              <ZmanimValue $ornament={ornament}>{formatTime(value)}</ZmanimValue>
            </ZmanimItem>
          ))}
          {/* Duplicate items only when continuous scrolling is enabled */}
          {autoScroll &&
            zmanim?.zmanimFormatted &&
            Object.entries(zmanim.zmanimFormatted).map(([key, value]) => (
              <ZmanimItem key={`duplicate-${key}`}>
                <ZmanimLabel $ornament={ornament}>
                  {key.replace(/([A-Z])/g, ' $1')
                    .replace(/^./, (str) => str.toUpperCase())}
                </ZmanimLabel>
                <ZmanimValue $ornament={ornament}>{formatTime(value)}</ZmanimValue>
              </ZmanimItem>
            ))}
        </ScrollContent>
      </ScrollContainer>
    </PanelRoot>
  );
};

export default ZmanimContainer; 